import React, { useState, useMemo } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { withRouter } from "react-router-dom";
import { Button, Divider, Form, Input, Select, Spin, Tag } from "antd";
import {
  EntityStatus,
  ConfigActions,
  ConfigEntity,
  formItemLayout,
  tailItemLayout,
  ConfigJSON,
} from "../../../Constants/Constants";
import {
  jsonValidator,
  selectFilterFunction,
} from "../../../Constants/Utilities";
import { getConfigFormTitle } from "../../../Helpers/ConfigHelpers";
import { onFormSubmit } from "../../../ApiHandlers/ConfigApiHandlers";
import { DiscardWarning } from "../../Common/DiscardWarning";
import { BreadCrumbs } from "../../Common/BreadCrumbs";

const getInitialValues = (record) => {
  const initialValues = {
    status: EntityStatus.ACTIVE,
    config: ConfigJSON,
  };
  if (record) {
    initialValues.name = record.name;
    initialValues.status = record.status;
    initialValues.serviceId = record.serviceId;
    initialValues.config = JSON.stringify(record.config, null, 4);
  }
  return initialValues;
};

const ServiceConfigForm = (props) => {
  console.log("ServiceConfigForm props", props);
  const { action, record, onActionClick, serviceDataLoading } = props;

  const title = useMemo(() => getConfigFormTitle(action), [action]);
  const [initialValues, setInitialValues] = useState(getInitialValues(record));

  const [form] = Form.useForm();

  const [processing, setProcessing] = useState(false);
  const isAddForm = props.action === ConfigActions.add;
  const isViewForm = props.action === ConfigActions.view;

  const onBackClick = () => {
    const isChanged =
      action === ConfigActions.view ? false : checkForInputChanges();
    DiscardWarning({ isChanged, onActionClick })();
  };

  const checkForInputChanges = () => {
    const fieldsToCheck = ["name", "providerId", "config", "serviceId"];
    const values = form.getFieldsValue();
    let isInputsChanged = false;
    fieldsToCheck.every((f) => {
      if (
        values.hasOwnProperty(f) &&
        values[f] !== initialValues[f] &&
        (!!values[f] || !!initialValues[f])
      ) {
        isInputsChanged = true;
        return false;
      }
      return true;
    });
    return isInputsChanged;
  };

  return (
    <div className="list-container">
      <div className="page-header-container">
        <BreadCrumbs
          headers={props.parentBread.concat(`${title} Service Configuration`)}
          onParentActionClick={onBackClick}
        />
      </div>
      <Divider className="divider" />
      <div className="form-data-container">
        <fieldset disabled={isViewForm}>
          <Form
            form={form}
            layout="horizontal"
            name="service-config-form"
            onFinish={onFormSubmit({
              props,
              setInitialValues,
              setProcessing,
              entity: ConfigEntity.service,
            })}
            initialValues={initialValues}
            requiredMark={"optional"}
            size={"middle"}
            {...formItemLayout}
          >
            <Spin spinning={processing} tip={"Processing, please wait..."}>
              <Form.Item
                name="name"
                label="Configuration Name"
                rules={[
                  { required: true, message: "Please fill in this field" },
                ]}
              >
                <Input
                  title="Name should be unique"
                  style={{ width: "90%", maxWidth: "300px" }}
                  disabled={!isAddForm}
                />
              </Form.Item>

              <Form.Item
                name="serviceId"
                label="Service:"
                rules={[{ required: true, message: "Please select Service" }]}
              >
                <Select
                  showSearch
                  allowClear
                  style={{ width: "90%", maxWidth: "350px" }}
                  loading={serviceDataLoading}
                  disabled={serviceDataLoading || !isAddForm}
                  filterOption={selectFilterFunction}
                  placeholder={
                    props.serviceDataLoading
                      ? "Loading Service list..."
                      : "Select Service"
                  }
                >
                  {props.serviceData.map((item) => (
                    <Select.Option
                      key={item.serviceId}
                      title={item.serviceName}
                    >
                      {item.serviceName}
                    </Select.Option>
                  ))}
                </Select>
              </Form.Item>

              <Form.Item
                name="config"
                label="Configuration"
                validateTrigger={["onBlur"]}
                rules={[{ required: true, validator: jsonValidator() }]}
              >
                <Input.TextArea
                  title="Configuration must be JSON object"
                  autoSize={{ minRows: 4, maxRows: 8 }}
                  style={{ width: "90%", maxWidth: "500px" }}
                />
              </Form.Item>

              <Form.Item
                name="status"
                label="Status"
                rules={[{ required: true }]}
              >
                <Tag
                  color={
                    initialValues.status === EntityStatus.ACTIVE
                      ? "green"
                      : "red"
                  }
                >
                  {initialValues.status}
                </Tag>
              </Form.Item>

              {!isViewForm && (
                <Form.Item {...tailItemLayout}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    className="submit-button"
                  >
                    {title}
                  </Button>
                  <Button
                    htmlType="button"
                    onClick={() => form.resetFields()}
                    className="cancel-button"
                  >
                    Cancel
                  </Button>
                </Form.Item>
              )}
            </Spin>
          </Form>
        </fieldset>
      </div>
    </div>
  );
};

ServiceConfigForm.propTypes = {
  action: PropTypes.string,
  record: PropTypes.object,
  serviceData: PropTypes.array,
  serviceDataLoading: PropTypes.bool,
  onSubmit: PropTypes.func,
  onActionClick: PropTypes.func,
  parentBread: PropTypes.array,

  configManagementApi: PropTypes.object,
};

const mapStateToProps = (state) => ({
  configManagementApi: state.apis.configManagementApi,
});
const connectedServiceConfigForm = connect(mapStateToProps)(ServiceConfigForm);
export default withRouter(connectedServiceConfigForm);
